import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";

/**
 * Efficiently checks individual book access for multiple books in a single query
 * This prevents N+1 query problems and makes book access instant
 */
export function useBatchIndividualAccess(bookIds: number[]) {
  const { session } = useAuth();

  return useQuery({
    queryKey: ['batchIndividualAccess', session?.user?.id, bookIds.sort()],
    queryFn: async () => {
      if (!session?.user?.id || bookIds.length === 0) {
        return new Map<number, boolean>();
      }

      // Fetch all individual book purchases for this user in one query
      const { data, error } = await supabase
        .from('individual_book_purchases')
        .select('book_id, access_enabled')
        .eq('user_id', session.user.id)
        .in('book_id', bookIds);

      if (error) {
        console.error('Error checking batch individual book access:', error);
        return new Map<number, boolean>();
      }

      // Create a map for O(1) lookups
      const accessMap = new Map<number, boolean>();
      data?.forEach(purchase => {
        accessMap.set(purchase.book_id, purchase.access_enabled);
      });

      return accessMap;
    },
    enabled: !!session?.user?.id && bookIds.length > 0,
    staleTime: 30000, // 30 seconds
    gcTime: 60000 // Keep in cache for 1 minute
  });
}

/**
 * Helper hook to check access for a single book from the batch
 */
export function useIndividualBookAccessFromBatch(bookId: number | null, batchAccessMap: Map<number, boolean> | undefined) {
  if (!bookId || !batchAccessMap) return false;
  return batchAccessMap.get(bookId) || false;
}
